<template>
	<div class="runPanel" :class="{ panelAttention: needsAttention, panelHistory: viewingHistory }">
		<!-- ==== Header ============================================================================================ -->
		<div class="runHeadline">
			<span class="stateBadge big" :class="runStatusBadgeClass(run.status)" :title="'Run status: ' + run.status">{{runStatusLabel(run.status)}}</span>
			<strong class="kindText">{{runKindLabel(run.kind)}}</strong>
			<span v-if="run.phase" class="phaseText">— {{run.phase}}</span>
			<span v-if="run.dryRun" class="stateBadge badgeOff" title="Simulated in dry-run mode; nothing touched a live server.">simulated (dry-run)</span>
			<span v-if="viewingHistory" class="stateBadge badgeInfo" title="A finished run opened from the history below; it is shown read-only.">from history</span>
			<button v-if="viewingHistory" type="button" class="linkButton" @click="$emit('back')">back to the current operation</button>
		</div>
		<div class="kvGrid">
			<span class="kvKey">Run</span>
			<span><code class="inline">{{run.runId || '?'}}</code><template v-if="run.origin"> · origin {{run.origin}}</template></span>
			<span class="kvKey">Started by</span>
			<span>{{run.actor || run.origin || 'unknown'}}</span>
			<span class="kvKey">Started</span>
			<span>{{formatDateTime(run.startedUtc)}} — <strong>{{elapsedText}}</strong> {{isActive ? 'elapsed so far' : 'total'}}</span>
			<template v-if="run.finishedUtc">
				<span class="kvKey">Finished</span>
				<span>{{formatDateTime(run.finishedUtc)}}</span>
			</template>
			<template v-if="run.sourceServer || run.targetServer">
				<span class="kvKey">Direction</span>
				<span>
					<code class="inline">{{run.sourceServer || '?'}}</code> → <code class="inline">{{run.targetServer || '?'}}</code>
					<template v-if="run.dbNames && run.dbNames.length"> ({{run.dbNames.join(', ')}})</template>
					<span v-if="run.targetDbNames && run.targetDbNames.length" class="mutedText"> — rename-mode rehearsal → {{run.targetDbNames.join(', ')}}</span>
				</span>
			</template>
			<template v-if="isActive && run.currentStepName">
				<span class="kvKey">Current step</span>
				<span class="currentStep"><span class="pulseDot"></span> {{run.currentStepName}}</span>
			</template>
			<template v-if="windowDeadline">
				<span class="kvKey">Window deadline</span>
				<span :class="{ deadlinePassed: windowDeadline.passed, deadlineSoon: windowDeadline.soon }">{{windowDeadline.text}}</span>
			</template>
			<template v-if="holdDeadline">
				<span class="kvKey">Hold deadline</span>
				<span :class="{ deadlinePassed: holdDeadline.passed, deadlineSoon: holdDeadline.soon }">{{holdDeadline.text}}<span class="mutedText"> — the configured hold-timeout action applies when it passes</span></span>
			</template>
			<template v-if="run.dbCutoverSeconds != null || run.webCutoverMs != null">
				<span class="kvKey">Measured cutover</span>
				<span>
					<template v-if="run.dbCutoverSeconds != null">database {{formatSeconds(run.dbCutoverSeconds)}}</template>
					<template v-if="run.dbCutoverSeconds != null && run.webCutoverMs != null"> · </template>
					<template v-if="run.webCutoverMs != null">web apps {{formatMs(run.webCutoverMs)}}</template>
				</span>
			</template>
		</div>

		<!-- ==== Problems ========================================================================================== -->
		<div v-if="run.error" class="errorBox"><strong>Error:</strong> {{run.error}}</div>
		<div v-if="run.manualFixInstructions" class="manualFixBox" :class="{ manualFixProminent: needsAttention }" role="alert">
			<div class="manualFixTitle">{{needsAttention ? 'MANUAL FIX REQUIRED' : 'Manual steps'}}</div>
			<pre class="manualFixText">{{run.manualFixInstructions}}</pre>
		</div>
		<div v-if="warnings.length" class="warningBox">
			<strong>{{warnings.length}} warning{{warnings.length === 1 ? '' : 's'}}:</strong>
			<ul>
				<li v-for="(w, i) in warnings" :key="i">{{w}}</li>
			</ul>
		</div>

		<!-- ==== Controls ========================================================================================== -->
		<div v-if="showControls" class="controlsBlock">
			<div class="sectionTitle">Controls</div>
			<div v-if="controlBusy" class="loadingInline"><ScaleLoader /> Sending "{{controlBusy}}"…</div>
			<template v-else>
				<div v-if="run.canApproveCutover" class="controlRow controlHighlight">
					<slide-button :key="slideKey('ApproveCutover')" :auto-width="false" :circle="true" :noanimate="true" :width="300" :height="50"
								  text="slide to approve cutover" success-text="approving" :disabled="disabled" :requireReleaseToComplete="true" @completed="$emit('control', 'ApproveCutover')" />
					<div class="controlText">
						<strong>The migration is holding at DbMigrate's operator gate.</strong>  Catch-up rounds keep the target current while it waits.
						Approving takes the final log backup, restores it and brings the target databases ONLINE, and then the web servers are
						cut over — the web apps are briefly unavailable while their app pools recycle.
					</div>
				</div>
				<div v-if="run.canResumeAfterHold" class="controlRow controlHighlight">
					<div class="controlStack">
						<slide-button :key="slideKey('ResumeAfterHold')" :auto-width="false" :circle="true" :noanimate="true" :width="300" :height="50"
									  text="slide to resume (restore leg)" success-text="resuming" :disabled="disabled" :requireReleaseToComplete="true" @completed="$emit('control', 'ResumeAfterHold')" />
						<div class="slideRed">
							<slide-button :key="slideKey('EndWindowAfterHold')" :auto-width="false" :circle="true" :noanimate="true" :width="300" :height="50"
										  text="slide to end the window" success-text="ending" :disabled="disabled" :requireReleaseToComplete="true" @completed="$emit('control', 'EndWindowAfterHold')" />
						</div>
					</div>
					<div class="controlText">
						<strong>Manual patch hold.</strong>  The databases are on the fallback and the primary is free for patching.
						<em>Resume</em> continues with the restore leg (migrate back to the primary and cut the web apps over to it).
						<em>End the window</em> stops here without restoring — the web apps stay on the fallback until someone restores traffic
						to the primary from this page.
					</div>
				</div>
				<div v-if="run.canStop" class="controlRow">
					<div class="slideRed">
						<slide-button :key="slideKey('Stop')" :auto-width="false" :circle="true" :noanimate="true" :width="300" :height="50"
									  text="slide to stop" success-text="stopping" :disabled="disabled" :requireReleaseToComplete="true" @completed="$emit('control', 'Stop')" />
					</div>
					<div class="controlText">
						Stops the operation at the next safe point.  A DbMigrate job cancels cleanly (the source stays live; target databases may
						be left RESTORING, which is resumable).  A web-app cutover that has already been sent to the agents completes on its own.
					</div>
				</div>
				<div v-if="run.canAcknowledge" class="controlRow">
					<slide-button :key="slideKey('Acknowledge')" :auto-width="false" :circle="true" :noanimate="true" :width="300" :height="50"
								  text="slide to acknowledge" success-text="acknowledging" :disabled="disabled" :requireReleaseToComplete="true" @completed="$emit('control', 'Acknowledge')" />
					<div class="controlText">Clears the attention banner.  The run and its outcome stay in the history.</div>
				</div>
			</template>
		</div>
		<div v-else-if="!viewingHistory && !permissionWrite && (run.canStop || run.canApproveCutover || run.canResumeAfterHold || run.canAcknowledge)" class="mutedText controlsReadOnly">
			This run offers controls, but you do not have the Online Server Maintenance Write permission.
		</div>

		<!-- ==== Steps ============================================================================================= -->
		<div class="sectionTitle">Steps</div>
		<div v-if="!steps.length" class="mutedText">No steps reported yet.</div>
		<div v-else class="stepList">
			<div v-for="s in steps" :key="s.id || s.name" class="stepRow" :class="stepStatusClass(s.status)">
				<span class="stepIcon" :title="s.status">{{stepStatusIcon(s.status)}}</span>
				<div class="stepBody">
					<div class="stepLine">
						<span class="stepName">{{s.name}}</span>
						<span class="stepStatus">{{s.status}}</span>
						<span v-if="s.startedUtc" class="mutedText stepTime">{{stepElapsedText(s)}}</span>
						<span v-if="s.attempt > 1" class="stateBadge badgeWarn" title="Retries so far">attempt {{s.attempt}}</span>
					</div>
					<div v-if="s.detail" class="stepDetail">{{s.detail}}</div>
				</div>
			</div>
		</div>

		<!-- ==== Migration detail ================================================================================== -->
		<template v-if="migration">
			<div class="sectionTitle">Database migration (DbMigrate)</div>
			<div class="kvGrid">
				<span class="kvKey">Job</span>
				<span><code class="inline">{{migration.jobId || '?'}}</code><template v-if="migration.createdUtc"> · created {{formatDateTime(migration.createdUtc)}}</template><template v-if="migration.finishedUtc"> · finished {{formatDateTime(migration.finishedUtc)}}</template></span>
				<span class="kvKey">Phase</span>
				<span>
					{{migration.phase || '?'}}
					<span v-if="migration.waitingForOperator" class="stateBadge badgeHold" title="DbMigrate is holding at its operator gate with catch-up rounds until the cutover is approved.">waiting at the cutover gate</span>
					<span v-else-if="migration.approved" class="stateBadge badgeGood">cutover approved</span>
				</span>
				<template v-if="migration.lastError">
					<span class="kvKey">Last error</span>
					<span class="problemText">{{migration.lastError}}</span>
				</template>
			</div>
			<ul v-if="migration.validationWarnings && migration.validationWarnings.length" class="warningList">
				<li v-for="(w, i) in migration.validationWarnings" :key="i">{{w}}</li>
			</ul>
			<div class="dbCards">
				<div v-for="db in migrationDatabases" :key="db.dbName" class="dbCard">
					<div class="dbCardHead">
						<span class="dbName">{{db.dbName}}</span>
						<span v-if="db.targetDbName && db.targetDbName !== db.dbName" class="mutedText">→ {{db.targetDbName}}</span>
						<span v-if="db.recoveryModel" class="stateBadge badgeOff" title="Recovery model of the source database">{{db.recoveryModel}}</span>
						<span v-if="db.targetCondition" class="mutedText" title="What DbMigrate found on the target">{{db.targetCondition}}</span>
					</div>
					<div class="miniSteps">
						<div v-for="s in (db.steps || [])" :key="s.id || s.name" class="miniStep" :class="stepStatusClass(s.status)" :title="(s.detail || '') + (s.attempt > 1 ? ' (attempt ' + s.attempt + ')' : '')">
							<span class="stepIcon small">{{stepStatusIcon(s.status)}}</span>
							<span class="miniStepName">{{s.name}}</span>
							<span v-if="s.startedUtc" class="mutedText miniStepTime">{{stepElapsedText(s)}}</span>
							<span v-if="s.attempt > 1" class="miniAttempt">×{{s.attempt}}</span>
						</div>
					</div>
					<div class="dbMetrics">
						<span v-if="db.catchUpIterations" title="Delta (log) catch-up rounds taken while waiting for the cutover">catch-up rounds: <strong>{{db.catchUpIterations}}</strong></span>
						<span v-if="db.cutoverDurationSeconds != null">
							cutover: <strong>{{formatSeconds(db.cutoverDurationSeconds)}}</strong>
							<span v-if="db.cutoverOvershoot" class="stateBadge badgeWarn" title="The final cutover took longer than the configured target.  This is a warning, never a failure.">over target</span>
						</span>
					</div>
					<div v-if="db.cutoverAttempts && db.cutoverAttempts.length" class="tableScroll">
						<table class="attemptTable">
							<thead>
								<tr>
									<th>Attempt</th>
									<th>Started</th>
									<th>Total</th>
									<th>Backup</th>
									<th>Identify</th>
									<th>Deliver</th>
									<th>Restore</th>
									<th>Result</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="a in db.cutoverAttempts" :key="a.attempt" :class="{ finalAttempt: a.isFinal }">
									<td>{{a.attempt}}<span v-if="a.isFinal" class="mutedText"> (final)</span></td>
									<td class="nowrap">{{formatUtc(a.startedUtc)}}</td>
									<td class="nowrap" :class="{ problemText: !a.metTimeout && !a.error }">{{formatSeconds(a.durationSeconds)}}</td>
									<td class="nowrap">{{formatSeconds(a.backupSeconds)}}</td>
									<td class="nowrap">{{formatSeconds(a.identifySeconds)}}</td>
									<td class="nowrap">{{formatSeconds(a.deliverSeconds)}}</td>
									<td class="nowrap">{{formatSeconds(a.restoreSeconds)}}</td>
									<td>
										<span v-if="a.error" class="problemText">{{a.error}}</span>
										<span v-else-if="a.metTimeout" class="okText">within target</span>
										<span v-else class="warnText">over target{{a.isFinal ? ' (accepted)' : ' — retried'}}</span>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</template>

		<!-- ==== Web-app cutover detail ============================================================================ -->
		<template v-if="cutover">
			<div class="sectionTitle">Web-app cutover</div>
			<div class="kvGrid">
				<span class="kvKey">Plan</span>
				<span><code class="inline">{{cutover.planId || '?'}}</code> · {{cutover.kind || 'Cutover'}}<span v-if="cutover.dryRun" class="stateBadge badgeOff"> simulated</span> · created {{formatDateTime(cutover.createdUtc)}}</span>
				<span class="kvKey">Phase</span>
				<span><span class="stateBadge" :class="cutoverPhaseBadgeClass(cutover.phase)">{{cutover.phase || '?'}}</span></span>
				<span class="kvKey">Servers</span>
				<span>
					taylor <code class="inline">{{cutover.sourceTaylorServer || '?'}}</code> → <code class="inline">{{cutover.targetTaylorServer || '?'}}</code>,
					umls <code class="inline">{{cutover.sourceUmlsServer || '?'}}</code> → <code class="inline">{{cutover.targetUmlsServer || '?'}}</code>
				</span>
				<span class="kvKey">Timeline</span>
				<span>
					database ONLINE {{formatUtc(cutover.dbOnlineUtc)}}
					<template v-if="cutover.firstCommitSentUtc"> → first commit sent {{formatUtc(cutover.firstCommitSentUtc)}}</template>
					<template v-if="cutover.lastServerHealthyUtc"> → last server healthy {{formatUtc(cutover.lastServerHealthyUtc)}}</template>
					<template v-if="cutover.cutoverDurationMs != null"> = <strong>{{formatMs(cutover.cutoverDurationMs)}}</strong></template>
				</span>
				<template v-if="commitDeadline">
					<span class="kvKey">Commit deadline</span>
					<span :class="{ deadlinePassed: commitDeadline.passed, deadlineSoon: commitDeadline.soon }">{{commitDeadline.text}}</span>
				</template>
				<template v-if="rollbackDeadline">
					<span class="kvKey">Rollback deadline</span>
					<span :class="{ deadlinePassed: rollbackDeadline.passed, deadlineSoon: rollbackDeadline.soon }">{{rollbackDeadline.text}}</span>
				</template>
				<template v-if="recoveryDeadline">
					<span class="kvKey">Recovery deadline</span>
					<span :class="{ deadlinePassed: recoveryDeadline.passed, deadlineSoon: recoveryDeadline.soon }">{{recoveryDeadline.text}}</span>
				</template>
			</div>
			<div v-if="cutoverServers.length" class="tableScroll">
				<table class="cutoverTable">
					<thead>
						<tr>
							<th>Web server</th>
							<th>State</th>
							<th>Attempts</th>
							<th>Last step</th>
							<th>Write</th>
							<th>Recycle</th>
							<th>Verify</th>
							<th>Observed effective (taylor / umls)</th>
							<th>Observed configured (taylor / umls)</th>
							<th>Observed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="s in cutoverServers" :key="s.agentId || s.agentName" :class="{ serverProblem: isServerProblem(s) }">
							<td>
								<div class="serverName">{{s.agentName || s.agentId}}</div>
								<div v-if="s.releaseName" class="mutedText small">release {{s.releaseName}}</div>
							</td>
							<td><span class="stateBadge" :class="serverStateBadgeClass(s.state)" :title="serverStateTitle(s.state)">{{s.state || '?'}}</span></td>
							<td>{{s.attempts}}</td>
							<td>
								<div v-if="s.lastStepId" class="mutedText small" :title="s.lastStepId">{{shortStepId(s.lastStepId)}}</div>
								<div v-if="s.lastOutcome">{{s.lastOutcome}}</div>
								<div v-if="s.error" class="problemText small">{{s.error}}</div>
							</td>
							<td class="nowrap">{{formatMs(s.writeMs)}}</td>
							<td class="nowrap">{{formatMs(s.recycleMs)}}</td>
							<td class="nowrap">{{formatMs(s.verifyMs)}}</td>
							<td class="nowrap"><code class="inline">{{s.observedEffectiveTaylorServer || '?'}}</code> / <code class="inline">{{s.observedEffectiveUmlsServer || '?'}}</code></td>
							<td class="nowrap"><code class="inline">{{s.observedConfiguredTaylorServer || '?'}}</code> / <code class="inline">{{s.observedConfiguredUmlsServer || '?'}}</code></td>
							<td class="nowrap">{{formatUtc(s.lastObservedUtc)}}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>

		<!-- ==== Log =============================================================================================== -->
		<template v-if="log.length">
			<div class="sectionTitle logTitle">
				<span>Recent log</span>
				<span class="mutedText small">{{log.length}} line{{log.length === 1 ? '' : 's'}}</span>
				<button type="button" class="linkButton" @click="logExpanded = !logExpanded">{{logExpanded ? 'shrink' : 'expand'}}</button>
			</div>
			<pre ref="logBox" class="logBox" :class="{ logExpanded: logExpanded }">{{log.join('\n')}}</pre>
		</template>
	</div>
</template>

<script>
	// Live view of one maintenance run (DbMaintenanceRunSnapshot): header, step list, DbMigrate per-database detail, web-app
	// cutover plan detail, warnings / error / manual fix instructions, recent log, and the run controls.  The parent owns the
	// polling and the ControlRun calls; this component emits `control(action)` (Stop | ApproveCutover | ResumeAfterHold |
	// EndWindowAfterHold | Acknowledge) and `back` (return from a history run to the current operation).
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import
		{
			runKindLabel, runStatusLabel, runStatusBadgeClass, stepStatusIcon, stepStatusClass, serverStateBadgeClass, cutoverPhaseBadgeClass,
			formatUtc, formatDateTime, formatElapsed, formatSeconds, formatMs, formatCountdown, isActiveRunStatus
		} from './runFormat';

	export default {
		components: { SlideButton },
		props:
		{
			/** DbMaintenanceRunSnapshot. */
			run: {
				type: Object,
				required: true
			},
			/** True when showing a run opened from the history (read-only; no controls). */
			viewingHistory: {
				type: Boolean,
				default: false
			},
			permissionWrite: {
				type: Boolean,
				default: false
			},
			/** The control action currently being sent, or null. */
			controlBusy: {
				type: String,
				default: null
			},
			/** Disables the slide controls while another action or dialog is in flight. */
			disabled: {
				type: Boolean,
				default: false
			},
			/** Incremented by the parent to reset the slide controls after an action. */
			slideCounter: {
				type: Number,
				default: 0
			}
		},
		data()
		{
			return {
				now: Date.now(),
				receivedAt: Date.now(), // When the current snapshot arrived; the elapsed readouts advance from it while the run is active.
				clockTimer: null,
				logExpanded: false
			};
		},
		computed:
		{
			isActive()
			{
				return isActiveRunStatus(this.run.status);
			},
			needsAttention()
			{
				return this.run.status === 'NeedsAttention';
			},
			/** Snapshot elapsed time plus the time since the snapshot arrived, while the run is still active. */
			elapsedText()
			{
				let ms = typeof this.run.elapsedMs === 'number' ? this.run.elapsedMs : 0;
				if (this.isActive)
					ms += Math.max(0, this.now - this.receivedAt);
				return formatElapsed(ms);
			},
			windowDeadline()
			{
				return formatCountdown(this.run.windowDeadlineUtc, this.now);
			},
			holdDeadline()
			{
				return formatCountdown(this.run.holdDeadlineUtc, this.now);
			},
			steps()
			{
				return Array.isArray(this.run.steps) ? this.run.steps : [];
			},
			warnings()
			{
				return Array.isArray(this.run.warnings) ? this.run.warnings : [];
			},
			log()
			{
				return Array.isArray(this.run.recentLog) ? this.run.recentLog : [];
			},
			migration()
			{
				return this.run.migration || null;
			},
			migrationDatabases()
			{
				return this.migration && Array.isArray(this.migration.databases) ? this.migration.databases : [];
			},
			cutover()
			{
				return this.run.cutover || null;
			},
			cutoverServers()
			{
				return this.cutover && Array.isArray(this.cutover.servers) ? this.cutover.servers : [];
			},
			commitDeadline()
			{
				return this.cutover ? formatCountdown(this.cutover.commitDeadlineUtc, this.now) : null;
			},
			rollbackDeadline()
			{
				return this.cutover ? formatCountdown(this.cutover.rollbackDeadlineUtc, this.now) : null;
			},
			recoveryDeadline()
			{
				return this.cutover ? formatCountdown(this.cutover.recoveryDeadlineUtc, this.now) : null;
			},
			showControls()
			{
				return !this.viewingHistory && this.permissionWrite
					&& !!(this.run.canStop || this.run.canApproveCutover || this.run.canResumeAfterHold || this.run.canAcknowledge);
			}
		},
		watch:
		{
			run()
			{
				this.receivedAt = Date.now();
				this.now = this.receivedAt;
				this.$nextTick(this.scrollLogToEnd);
			}
		},
		mounted()
		{
			this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
			this.scrollLogToEnd();
		},
		beforeUnmount()
		{
			clearInterval(this.clockTimer);
		},
		methods:
		{
			runKindLabel,
			runStatusLabel,
			runStatusBadgeClass,
			stepStatusIcon,
			stepStatusClass,
			serverStateBadgeClass,
			cutoverPhaseBadgeClass,
			formatUtc,
			formatDateTime,
			formatElapsed,
			formatSeconds,
			formatMs,
			/** A slide control resets when the run, its status or the parent's counter changes — not on every 2 s snapshot. */
			slideKey(action)
			{
				return 'slide_run_' + action + '_' + (this.run.runId || '') + '_' + (this.run.status || '') + '_' + this.slideCounter;
			},
			/** Elapsed time of a step; a running step's readout advances from the snapshot time. */
			stepElapsedText(s)
			{
				let ms = typeof s.elapsedMs === 'number' ? s.elapsedMs : 0;
				if (s.status === 'Running' && !s.endedUtc && this.isActive)
					ms += Math.max(0, this.now - this.receivedAt);
				return formatElapsed(ms);
			},
			isServerProblem(s)
			{
				return s.state === 'Split' || s.state === 'Unreachable' || s.state === 'Failed' || !!s.error;
			},
			serverStateTitle(state)
			{
				switch (state)
				{
					case 'Pending': return 'Nothing has been sent to this server yet.';
					case 'Sent': return 'The cutover step was sent; waiting for the agent to write, recycle and verify.';
					case 'OnTarget': return 'Verified: the web apps on this server are effectively connected to the target.';
					case 'OnSource': return 'Verified: the web apps on this server are effectively connected to the source (not yet cut over, or rolled back).';
					case 'Split': return 'DANGER: this server disagrees with itself or the fleet (e.g. configured and effective servers differ).';
					case 'Unreachable': return 'The agent or its health endpoints could not be reached; the orchestrator keeps retrying until the deadline.';
					case 'Failed': return 'The cutover step failed on this server.';
					default: return state || '';
				}
			},
			/** Step IDs are planId:agentId:attemptN; show the tail so the table stays narrow. */
			shortStepId(id)
			{
				if (!id)
					return '';
				let parts = String(id).split(':');
				return parts.length > 1 ? '…:' + parts[parts.length - 1] : id;
			},
			scrollLogToEnd()
			{
				let box = this.$refs.logBox;
				if (box)
					box.scrollTop = box.scrollHeight;
			}
		}
	}
</script>

<style scoped>
	.runPanel
	{
		padding: 2px 0;
	}

	.runHeadline
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 12px;
		margin-bottom: 8px;
		font-size: 1.1em;
	}

	.kindText
	{
		font-size: 1.1em;
	}

	.phaseText
	{
		color: #555555;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 6px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	.mutedText
	{
		color: #888888;
	}

	.small
	{
		font-size: 0.85em;
	}

	.nowrap
	{
		white-space: nowrap;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}

	.linkButton
	{
		background: none;
		border: none;
		padding: 0;
		color: #1A5FB4;
		text-decoration: underline;
		cursor: pointer;
		font: inherit;
		font-size: 0.9em;
	}

	.currentStep
	{
		font-weight: bold;
		color: #123F7A;
	}

	.pulseDot
	{
		display: inline-block;
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background-color: #3A7BD5;
		animation: runPulse 1.4s ease-in-out infinite;
		vertical-align: middle;
	}

	@keyframes runPulse
	{
		0%, 100%
		{
			opacity: 1;
		}

		50%
		{
			opacity: 0.25;
		}
	}

	.deadlineSoon
	{
		color: #7A5200;
		font-weight: bold;
	}

	.deadlinePassed
	{
		color: #8A0000;
		font-weight: bold;
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin: 8px 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin: 8px 0;
	}

		.warningBox ul
		{
			margin: 4px 0 0 0;
			padding-left: 22px;
		}

	.warningList
	{
		margin: 4px 0;
		padding-left: 22px;
		color: #7A5200;
	}

	.manualFixBox
	{
		background-color: #FFF3E0;
		border: 1px solid #E0B93F;
		color: #3A2000;
		padding: 8px 12px;
		margin: 8px 0;
	}

		.manualFixBox.manualFixProminent
		{
			background-color: #FFE0E0;
			border: 3px solid #C00000;
			color: #4A0000;
		}

	.manualFixTitle
	{
		font-weight: bold;
		font-size: 1.1em;
		margin-bottom: 4px;
	}

	.manualFixText
	{
		font-family: inherit;
		white-space: pre-wrap;
		word-break: break-word;
		margin: 0;
		line-height: 1.5;
	}

	.sectionTitle
	{
		font-weight: bold;
		font-size: 1.05em;
		border-bottom: 1px solid #E0E0E0;
		padding-bottom: 3px;
		margin: 14px 0 6px 0;
	}

	/* ---- Controls ------------------------------------------------------------------------------------------------ */
	.controlsBlock
	{
		margin-top: 6px;
	}

	.controlsReadOnly
	{
		margin-top: 8px;
		font-style: italic;
	}

	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.controlRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 10px 18px;
		padding: 8px 6px;
		border-bottom: 1px solid #F0F0F0;
	}

		.controlRow:last-child
		{
			border-bottom: none;
		}

		.controlRow.controlHighlight
		{
			background-color: #F4F0FF;
			border: 1px solid #B39DE0;
			border-radius: 4px;
			margin-bottom: 6px;
		}

	.controlStack
	{
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.controlText
	{
		flex: 1 1 300px;
		font-size: 0.92em;
		color: #333333;
		line-height: 1.5;
	}

	.controlRow :deep(.slideunlock)
	{
		margin: 0;
	}

	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
	}

	/* ---- Steps ---------------------------------------------------------------------------------------------------- */
	.stepList
	{
		margin-top: 4px;
	}

	.stepRow
	{
		display: flex;
		gap: 10px;
		padding: 5px 4px;
		border-bottom: 1px solid #F0F0F0;
	}

		.stepRow:last-child
		{
			border-bottom: none;
		}

		.stepRow.stepRunning
		{
			background-color: #EEF4FC;
		}

		.stepRow.stepWarning
		{
			background-color: #FFF8E6;
		}

		.stepRow.stepFailed
		{
			background-color: #FFF0F0;
		}

		.stepRow.stepPending .stepName,
		.stepRow.stepSkipped .stepName
		{
			color: #888888;
		}

	.stepIcon
	{
		flex: 0 0 22px;
		width: 22px;
		height: 22px;
		line-height: 22px;
		text-align: center;
		border-radius: 50%;
		font-weight: bold;
		color: #FFFFFF;
		background-color: #BBBBBB;
		cursor: help;
	}

		.stepIcon.small
		{
			flex: 0 0 16px;
			width: 16px;
			height: 16px;
			line-height: 16px;
			font-size: 0.75em;
		}

	.stepSucceeded .stepIcon
	{
		background-color: #2E9E2E;
	}

	.stepWarning .stepIcon
	{
		background-color: #E0B93F;
		color: #3A2000;
	}

	.stepFailed .stepIcon
	{
		background-color: #CC3333;
	}

	.stepRunning .stepIcon
	{
		background-color: #3A7BD5;
	}

	.stepCancelled .stepIcon
	{
		background-color: #888888;
	}

	.stepBody
	{
		flex: 1 1 auto;
		min-width: 0;
	}

	.stepLine
	{
		display: flex;
		flex-wrap: wrap;
		gap: 4px 10px;
		align-items: baseline;
	}

	.stepName
	{
		font-weight: bold;
	}

	.stepStatus
	{
		font-size: 0.9em;
		color: #555555;
	}

	.stepFailed .stepStatus
	{
		color: #8A0000;
		font-weight: bold;
	}

	.stepTime
	{
		font-size: 0.85em;
	}

	.stepDetail
	{
		font-size: 0.9em;
		color: #444444;
		white-space: pre-wrap;
		word-break: break-word;
		margin-top: 2px;
	}

	/* ---- Migration ------------------------------------------------------------------------------------------------ */
	.dbCards
	{
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
		gap: 10px;
		margin-top: 6px;
	}

	.dbCard
	{
		border: 1px solid #E0E0E0;
		border-radius: 4px;
		padding: 8px 10px;
		background-color: #FAFAFA;
	}

	.dbCardHead
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 4px;
	}

	.dbName
	{
		font-weight: bold;
		font-size: 1.1em;
	}

	.miniSteps
	{
		display: flex;
		flex-wrap: wrap;
		gap: 4px 12px;
		margin: 4px 0;
	}

	.miniStep
	{
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 2px 8px 2px 4px;
		border: 1px solid #E4E4E4;
		border-radius: 12px;
		background-color: #FFFFFF;
		font-size: 0.9em;
	}

		.miniStep.stepRunning
		{
			border-color: #7FAEE8;
			background-color: #EEF4FC;
		}

		.miniStep.stepFailed
		{
			border-color: #CC6666;
			background-color: #FFF0F0;
		}

		.miniStep.stepWarning
		{
			border-color: #E0B93F;
			background-color: #FFF8E6;
		}

		.miniStep.stepPending .miniStepName,
		.miniStep.stepSkipped .miniStepName
		{
			color: #888888;
		}

	.miniStepTime
	{
		font-size: 0.85em;
	}

	.miniAttempt
	{
		color: #7A5200;
		font-weight: bold;
		font-size: 0.85em;
	}

	.dbMetrics
	{
		display: flex;
		flex-wrap: wrap;
		gap: 4px 16px;
		font-size: 0.92em;
		margin: 4px 0;
	}

	.tableScroll
	{
		overflow-x: auto;
	}

	.attemptTable,
	.cutoverTable
	{
		border-collapse: collapse;
		width: 100%;
		font-size: 0.88em;
		margin-top: 4px;
	}

		.attemptTable th,
		.attemptTable td,
		.cutoverTable th,
		.cutoverTable td
		{
			text-align: left;
			padding: 3px 7px;
			border-bottom: 1px solid #E4E4E4;
			vertical-align: top;
		}

		.attemptTable th,
		.cutoverTable th
		{
			color: #555555;
			font-size: 0.9em;
			white-space: nowrap;
		}

		.attemptTable tr.finalAttempt td
		{
			font-weight: bold;
		}

		.cutoverTable tr.serverProblem td
		{
			background-color: #FFF0F0;
		}

	.serverName
	{
		font-weight: bold;
	}

	.problemText
	{
		color: #8A0000;
		font-weight: bold;
	}

	.okText
	{
		color: #0A5A0A;
	}

	.warnText
	{
		color: #7A5200;
	}

	/* ---- Log ------------------------------------------------------------------------------------------------------ */
	.logTitle
	{
		display: flex;
		align-items: baseline;
		gap: 12px;
	}

	.logBox
	{
		font-family: Consolas, monospace;
		font-size: 0.82em;
		background-color: #1E1E1E;
		color: #D4D4D4;
		border: 1px solid #444444;
		padding: 8px 10px;
		margin: 4px 0 0 0;
		max-height: 220px;
		overflow: auto;
		white-space: pre-wrap;
		word-break: break-word;
	}

		.logBox.logExpanded
		{
			max-height: 70vh;
		}

	/* ---- Badges --------------------------------------------------------------------------------------------------- */
	.stateBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
	}

		.stateBadge.big
		{
			font-size: 1.05em;
			padding: 3px 14px;
		}

	.badgeGood
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.badgeWarn
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.badgeBad
	{
		background-color: #FFE0E0;
		color: #8A0000;
		border: 1px solid #CC6666;
	}

	.badgeInfo
	{
		background-color: #DDEBFF;
		color: #123F7A;
		border: 1px solid #7FAEE8;
	}

	.badgeHold
	{
		background-color: #EADDFF;
		color: #3F1F7A;
		border: 1px solid #B39DE0;
	}

	.badgeOff
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}
</style>
